import { CSSProperties, ReactNode, useMemo, useState } from 'react';
import { UniqueIdentifier } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, SxProps, Theme } from '@rossum/ui/material';
import { TileDraggableProvider } from './TileDraggableContext';
import { TileDraggableContextValue, TileDraggableState } from './types';

type TileDraggableProps = {
  /** Unique identifier for the sortable item */
  id: UniqueIdentifier;
  /** Whether dragging is disabled */
  disabled?: boolean;
  /** Content to render - can be a function receiving drag state */
  children: ReactNode | ((state: TileDraggableState) => ReactNode);
  /** Custom styles for the wrapper */
  sx?: SxProps<Theme>;
  /** Data attribute for testing */
  'data-cy'?: string;
};

export const TileDraggable = ({
  id,
  disabled,
  children,
  sx,
  'data-cy': dataCy,
}: TileDraggableProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const {
    attributes,
    listeners,
    isDragging,
    transition,
    transform,
    setNodeRef,
  } = useSortable({ id, disabled });

  const isActive = isHovered || isFocused;
  const isPlaceholder = isDragging;

  const state: TileDraggableState = useMemo(
    () => ({
      isActive,
      isDragging,
      isPlaceholder,
    }),
    [isActive, isDragging, isPlaceholder]
  );

  const contextValue: TileDraggableContextValue = useMemo(
    () => ({
      ...state,
      handleProps: {
        ...listeners,
        ...attributes,
      },
    }),
    [state, listeners, attributes]
  );

  const style: CSSProperties = useMemo(() => {
    const scale = isPlaceholder ? 1.02 : 1;

    return {
      transform: CSS.Transform.toString({
        x: transform?.x ?? 0,
        y: transform?.y ?? 0,
        scaleX: scale,
        scaleY: scale,
      }),
      transition,
    };
  }, [transform, transition, isPlaceholder]);

  return (
    <TileDraggableProvider value={contextValue}>
      <Box
        ref={setNodeRef}
        style={style}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        data-cy={dataCy}
        sx={{
          opacity: isDragging ? 0.5 : 1,
          ...sx,
        }}
      >
        {typeof children === 'function' ? children(state) : children}
      </Box>
    </TileDraggableProvider>
  );
};
